/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import { Link } from 'react-router-dom';
import { Button, Icon, ProgressCircle, useToast } from '@coreai/component-library';
import axios from 'axios';
import { API_URLS } from "../../../services/config";
import { verifyCaptcha } from "services/userServices";
import { useLanguage } from 'LanguageContext';
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";

const ForgotPasswordForm = () => {
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaValue, setCaptchaValue] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { t } = useLanguage();

  const fetchCaptcha = async () => {
    try {
      const response = await axios.get(`${API_URLS.CAPTCH_LOAD}`, {
        responseType: 'arraybuffer',
      });
      const base64 = btoa(
        new Uint8Array(response.data)
          .reduce((data, byte) => data + String.fromCharCode(byte), '')
      );
      setCaptchaValue(`data:image/png;base64,${base64}`);
    } catch (error) {
      console.error('Captcha load error:', error);
    }
  };

  useEffect(() => {
    fetchCaptcha();
  }, []);

  const refreshCaptcha = () => {
   fetchCaptcha();
   setCaptchaInput('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('email', email);
      formData.append('captcha', captchaInput);
      setIsLoading(true);
      verifyCaptcha(formData).then((result: any) => {
                if(result.success){
                      toast({
                        variant: 'success',
                        title: result?.message,
                      });
                      setSubmitted(true);
                      refreshCaptcha();
                      setIsLoading(false);
                }else{
                  toast({
                    variant: 'error',
                    title: result?.message,
                  });
                  setCaptchaInput('');
                  refreshCaptcha();
                  setIsLoading(false);
                }
              }).catch((error: any) => {
                console.log({error},'updateUserConsent')
                refreshCaptcha();
                  toast({
                  variant: 'error',
                  title: error?.response?.data?.message,
                  });
                  setIsLoading(false);
              })
    } catch (error) {
      
    }
  };

  const handleResend = () => {
    setEmail('');
    setSubmitted(false);
  };

  const containerStyle = css`
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f3f4f6;
    padding: 1rem;
  `;

  const cardStyle = css`
    background: white;
    padding: 2.5rem;
    border-radius: 0.5rem;
    box-shadow: 0px 4px 24px 0px #0000001A;
    width: 100%;
    max-width: 450px;
  `;

  const headingStyle = css`
    font-size: 30px;
    font-weight: 600;
    line-height: 120%;
    margin-bottom: 1.25rem;

    @media (max-width: 768px) {
      font-size: 20px;
      margin-bottom: 1rem;
    }
  `;

  const descriptionStyle = css`
    font-size: 14px;
    font-weight: 500;
    line-height: 150%;
    color: #3D5360;
    margin-bottom: 2rem;

    @media (max-width: 768px) {
      margin-bottom: 1rem;
    }
  `;

  const labelStyle = css`
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    margin-bottom: 0.25rem;
  `;

  const inputStyle = css`
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid #acb8bd;
    border-radius: 0.5rem;
    margin-bottom: 1rem;
    &:focus {
      outline: none;
      border-color: ${theme.colors.primary};
      box-shadow: 0 0 0 2px ${theme.colors.primary}33;
    }
  `;

  const buttonStyle = css`
    width: 100%;
    background-color: ${theme.colors.primary};
    color: white;
    padding: 0.5rem;
    border: none;
    border-radius: 1.25rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s ease;
  `;

  const linkStyle = css`
    display: block;
    text-align: center;
    margin-top: 1.5rem;
    font-size: 0.875rem;
    color: ${theme.colors.primary};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  `;

  const resendLinkStyle = css`
    font-weight: 500;
    color: ${theme.colors.primary};
    cursor: pointer;
    text-decoration: underline;
    margin-top: 1rem;
    display: inline-block;

    &:hover {
      opacity: 0.85;
    }
  `;

  const captchaMaindiv = css`
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    gap: 12px;
    
    img {
      height: 50px;
      width: 180px;
      border-radius: 6px;
      box-shadow: 8px 8px 24px 2px #0000001A;
    }
  `;

  const refreshIconStyle = css`
    cursor: pointer;
  `;

  return (
    <div css={containerStyle}>
      <div css={cardStyle}>
        {!submitted ? (
          <form onSubmit={handleSubmit}>
            <h2 css={headingStyle}>{t("change_password_reset_title")}</h2>
            <p css={descriptionStyle}>
              {t("change_password_desc")}
            </p>

            <label css={labelStyle}>{t('signin_email')}</label>
            <input
              type="email"
              placeholder={t('reset_password_enter_email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              css={inputStyle}
              required
            />

            <div>
              <div css={captchaMaindiv}>
                <img src={captchaValue} alt="captach" />
                <Icon
                    css={refreshIconStyle}
                    id="captchabtn"
                    onClick={() => refreshCaptcha()}
                    name="refresh" 
                    size="md"
                />
              </div>
              <label css={labelStyle}>{t('change_password_captcha')}</label>
              <input
                required
                type="text"
                name="captchaInput"
                data-testid="captchaInput"
                placeholder={t('reset_password_enter_captcha')}
                value={captchaInput}
                css={inputStyle}
                onChange={(event) => {
                  setCaptchaInput(event.target.value);
                }}
                />
            </div>

            <Button type="submit" css={buttonStyle} disabled={captchaInput === '' || email === '' || isLoading}>
              { isLoading ? (
                <ProgressCircle size="xxs" color={theme.colors.primary} />
              ) : t('change_password_continue')}</Button>
          </form>
        ) : (
          <div>
            <h2 css={headingStyle}>{t('check_your_email_title')}</h2>
            <p css={descriptionStyle}>
              <Markdown  rehypePlugins={[rehypeRaw]}>
                {t("check_your_email_desc", {
                  email: email
                })}
              </Markdown>
              <br /><br />
                {t('check_your_email_if_not')}{' '}
              <span css={resendLinkStyle} onClick={handleResend}>{t('check_your_email_resend_link')}</span>
            </p>

          </div>
        )}
        <Link to="/signin" css={linkStyle}>
            {t('check_your_email_signin_link')}
        </Link>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;